'use client';

import { EditableTextLeadingAdornmentSlot } from '@magicschool/spellbook/components/EditableText/slots';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Text, type TextProps, textVariants } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Handle exposed by the EditableText component via forwardRef.
 * Provides imperative access to both the display text element and the input/textarea element.
 *
 * @TODO Explain this somewhere in docs?
 */
export type EditableTextHandle = {
  /** Reference to the display text element shown when not in edit mode. */
  text: HTMLElement | null;
  /** Reference to the input or textarea element used during editing. */
  input: HTMLInputElement | HTMLTextAreaElement | null;
};

type BaseEditableTextProps = React.HTMLAttributes<HTMLElement> &
  VariantProps<typeof textVariants> &
  Omit<VariantProps<typeof editableWrapperVariants>, 'isEditing' | 'size'> & {
    /** The HTML element to render for the display text. Defaults to 'span'. */
    element?: TextProps['element'];
    ref?: React.Ref<HTMLElement>;
    /** The current value of the editable text. When provided, the component becomes controlled. */
    value?: string;
    /** The default value for uncontrolled usage. */
    defaultValue?: string;
    /** Callback fired when the input value changes during editing. Receives the new value as an argument. */
    onInputChange?: (value: string) => void;
    /** Callback fired when the value is saved via Enter key or Save button. */
    onSave?: (value: string) => void;
    /** Callback fired when editing is cancelled via Escape key or Cancel button. */
    onCancel?: () => void;
    /** The draft value to use while editing. When provided along with onDraftChange, allows external control of the draft state. */
    draftValue?: string;
    /** Callback fired when the draft value changes. Use with draftValue for external draft state control. */
    onDraftChange?: (value: string) => void;
    /** The input type. Only 'text' is supported for multiline mode. Defaults to 'text'. */
    type?: 'text' | 'number';
    /** Whether to show the edit button unless hovering. */
    showEditButtonUnlessHovering?: boolean;
    /** Whether to use a multiline textarea instead of a single-line input. Only supported with 'heading-1', 'body-1', 'body-2', 'label-1', 'label-2', or 'badge-1' text variants and type 'text'. */
    multiline?: boolean;
    /** Whether to show the invalid state (error outline). */
    invalid?: boolean;
    /** Additional class name to apply to the display text element. */
    textClassName?: string;
    /** Additional class name to apply to the input/textarea element. */
    inputClassName?: string;
    /** Whether to disable saving changes when the input loses focus. */
    disableSaveOnBlur?: boolean;
    /** Whether to disable cancelling edits when pressing the Escape key. */
    disableCancelOnEscape?: boolean;
    /** Whether to disable saving changes when pressing the Enter key. (single line only) */
    disableSaveOnEnter?: boolean;
    /** Whether to truncate text with ellipsis when it overflows. Only applies to single-line mode. Defaults to false. */
    truncate?: boolean;
    /** Whether the component is in edit mode. When provided, the component becomes controlled for editing state. */
    isEditing?: boolean;
    /** Callback fired when the editing state changes. */
    onIsEditingChange?: (isEditing: boolean) => void;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** Label for the edit button. Defaults to 'Edit'. */
      editButtonLabel?: string;
      /** Placeholder text shown when the value is empty. Defaults to 'No text'. */
      textPlaceholder?: string;
      /** Placeholder for the input field during editing. Defaults to 'Enter text'. */
      inputPlaceholder?: string;
      /** Accessible label for the input field used by screen readers. Defaults to 'Edit text'. */
      inputLabel?: string;
      /** Screen reader announcement when saving changes. Defaults to 'Text saved'. */
      saveAnnouncement?: string;
      /** Screen reader announcement when cancelling edits. Defaults to 'Edit canceled'. */
      cancelAnnouncement?: string;
      /** Label for the save button. Defaults to 'Save'. */
      saveButtonLabel?: string;
      /** Label for the cancel button. Defaults to 'Cancel'. */
      cancelButtonLabel?: string;
    };
  };

/**
 * Conditionally uses `<input>` or `<textarea>` props based on the `multiline` prop value.
 */
export type EditableTextProps =
  | (BaseEditableTextProps &
      Omit<React.ComponentProps<'input'>, 'size' | 'type' | 'value' | 'defaultValue' | 'onChange'> & {
        multiline?: false;
      })
  | (BaseEditableTextProps &
      Omit<React.ComponentProps<'textarea'>, 'value' | 'defaultValue' | 'rows' | 'onChange'> & {
        multiline: true;
      });

const editableWrapperVariants = cva(
  [
    'group/editable-text',
    'before:bg-surface-transparent-0',
    '[&:has(*:focus-visible)]:before:bg-surface-transparent-1',
    'before:absolute',
    'before:inset-0',
    'before:pointer-events-none',
    'before:transition-colors',
    'relative',

    'transition-colors',
    'before:border',
    'before:border-transparent',
  ],
  {
    variants: {
      /* Whether the editable text should take the full width of its container. */
      fullWidth: {
        true: 'w-full flex',
        false: 'w-auto inline-flex max-w-full',
      },
      size: {
        xs: ['p-1', '-translate-x-1', 'before:rounded-control-xs', 'rounded-control-xs'],
        sm: ['px-2', 'py-1', '-translate-x-2', 'before:rounded-control-sm', 'rounded-control-sm'],
      },
      multiline: {
        true: '',
        false: 'max-w-full',
      },
      invalid: {
        true: [
          'before:border-error-stroke',
          'hover:before:border-error-stroke',
          'focus-within:before:border-error-stroke',
          'hover:focus-within:before:border-error-stroke',
        ],
        false: '',
      },
      truncate: {
        true: 'max-w-full overflow-x-hidden',
        false: '',
      },
      disabled: {
        true: '',
        false: 'hover:before:bg-surface-transparent-1',
      },
    },
    compoundVariants: [
      {
        truncate: true,
        size: ['xs', 'sm'],
        className: 'py-1',
      },
    ],
  },
);

const editableInputWrapperVariants = cva(
  [
    'absolute',
    'inset-0',
    'flex',
    'justify-between',
    'ring-1',
    'ring-inset',
    'ring-border-default',
    'hover:ring-focus-ring',
    'focus-within:ring-focus-ring',
    'bg-surface-background-0',
    'appearance-none',
    'cursor-text',
    'min-w-full',
  ],
  {
    variants: {
      size: {
        xs: 'px-1 gap-1 rounded-control-xs',
        sm: 'px-2 gap-2 rounded-control-sm',
      },
      multiline: {
        true: 'items-start',
        false: 'items-center',
      },
      invalid: {
        true: '!ring-error-stroke',
        false: '',
      },
      truncate: {
        true: 'max-w-full',
        false: '',
      },
      isEditing: {
        true: 'py-1 animate-in transition-colors h-fit',
        false: 'py-0 pointer-events-none h-0 overflow-hidden transition-none',
      },
    },
    compoundVariants: [
      {
        truncate: true,
        isEditing: true,
        size: ['xs', 'sm'],
        className: 'py-1',
      },
    ],
  },
);

const editIconButtonVariants = cva(
  [
    'overflow-visible', // Ensure the button is fully visible
    'h-0', // Start with height 0 to prevent layout shift
    'opacity-0',
    'transition-opacity',
    'group-hover/editable-text:opacity-100',
    'focus-visible:opacity-100',
    'focus-visible:!ring-0',
    'focus-visible:!ring-offset-0',
    'focus-visible:after:ring-2',
    'focus-visible:after:ring-inset',
    'focus-visible:after:ring-focus-ring',
    'active:bg-surface-transparent-0',
    'justify-self-end',
    'hover:bg-surface-transparent-0',
    'after:absolute',
    'after:bg-surface-transparent-0',
    'after:inset-0',
  ],
  {
    variants: {
      size: {
        xs: 'w-6 after:rounded-control-xs',
        sm: 'w-9 after:rounded-control-sm',
      },
      isEditing: {
        true: 'focus-visible:opacity-0',
        false: '',
      },
      multiline: {
        true: 'self-start mt-3.25',
        false: '',
      },
    },
  },
);

const editableInputVariants = cva('min-h-fit w-full grow-1', {
  variants: {
    size: {
      xs: '',
      sm: '',
    },
    multiline: {
      true: 'resize-none overflow-hidden min-h-0 [field-sizing:content]',
      false: '',
    },
    hasLeadingAdornment: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      multiline: true,
      hasLeadingAdornment: true,
      className: 'py-1',
    },
  ],
});

const editableAdornmentVariants = cva('h-0 flex items-center overflow-visible', {
  variants: {
    multiline: {
      true: 'self-start pt-3.25',
      false: '',
    },
    leading: {
      true: '',
      false: '',
    },
  },
});

const editableTextVariants = cva('h-auto shrink-0 grow-1', {
  variants: {
    hasValue: {
      true: '',
      false: '!text-placeholder',
    },
    multiline: {
      true: 'whitespace-pre-line max-w-[calc(100%-2rem)]',
      false: 'truncate max-w-[calc(100%-1.75rem)]',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
    isEditing: {
      true: 'opacity-0',
      false: '',
    },
    hasLeadingAdornment: {
      true: '',
      false: '',
    },
    size: {
      xs: 'min-w-24',
      sm: 'min-w-30',
    },
    disabled: {
      true: 'max-w-full',
    },
  },
  compoundVariants: [
    {
      size: 'xs',
      isEditing: true,
      className: 'pe-6 min-h-4',
    },
    {
      size: 'sm',
      isEditing: true,
      className: 'pe-20 min-h-6',
    },
    {
      truncate: true,
      size: ['xs', 'sm'],
      className: 'min-w-0',
    },
  ],
});

const EditableTextBase = forwardRef<EditableTextHandle, EditableTextProps>(
  (
    {
      element,
      className,
      disableCancelOnEscape,
      disableSaveOnBlur,
      disableSaveOnEnter,
      textClassName,
      inputClassName,
      multiline = false,
      truncate: providedTruncate = false,
      showEditButtonUnlessHovering = false,
      color = 'default',
      disabled,
      invalid = false,
      variant = 'body-2',
      type = 'text',
      value,
      fullWidth = false,
      defaultValue,
      id: providedId,
      onInputChange,
      onSave,
      onCancel,
      draftValue: providedDraftValue,
      onDraftChange,
      isEditing: providedIsEditing,
      onIsEditingChange,
      children,
      text = {},
      ...props
    },
    forwardedRef,
  ) => {
    const editableWrapperRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    const {
      editButtonLabel = 'Edit',
      textPlaceholder = 'No text',
      inputLabel = 'Edit text',
      inputPlaceholder = 'Enter text',
      saveAnnouncement = 'Text saved',
      cancelAnnouncement = 'Edit canceled',
      saveButtonLabel = 'Save',
      cancelButtonLabel = 'Cancel',
    } = text;

    const id = useId(providedId);
    const size = ['heading-1', 'display-1'].includes(variant!) ? 'sm' : 'xs';

    const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');
    const [announcement, setAnnouncement] = useState<string>('');
    const [internalDraftValue, setInternalDraftValue] = useState<string>('');
    const [internalIsEditing, setInternalIsEditing] = useState(false);

    const isControlled = value !== undefined;
    const isDraftControlled = providedDraftValue !== undefined;
    const isEditingControlled = providedIsEditing !== undefined;

    const currentValue = isControlled ? value : internalValue;
    const draftValue = isDraftControlled ? providedDraftValue : internalDraftValue;
    const isEditing = isEditingControlled ? providedIsEditing : internalIsEditing;
    const truncate = !multiline && providedTruncate; // truncation only applies to single-line option

    const setDraftValue = (newDraftValue: string) => {
      if (!isDraftControlled) {
        setInternalDraftValue(newDraftValue);
      }
      onDraftChange?.(newDraftValue);
    };

    const setIsEditing = useCallback(
      (newIsEditing: boolean) => {
        if (!isEditingControlled) {
          setInternalIsEditing(newIsEditing);
        }
        onIsEditingChange?.(newIsEditing);

        if (newIsEditing) {
          requestAnimationFrame(() => {
            if (multiline) {
              textAreaRef.current?.focus();
              textAreaRef.current?.setSelectionRange(textAreaRef.current.value.length, textAreaRef.current.value.length);
            } else {
              inputRef.current?.focus();
              inputRef.current?.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
            }
          });
        }
      },
      [multiline],
    );

    // Expose imperative methods for three accessible refs in component
    useImperativeHandle(
      forwardedRef,
      () => ({
        text: textRef.current,
        input: multiline ? textAreaRef.current : inputRef.current,
      }),
      [multiline, isEditing],
    );

    useLayoutEffect(() => {
      if (!textAreaRef.current || !editableWrapperRef.current) return;
      const textareaEl = textAreaRef.current;

      requestAnimationFrame(() => {
        textareaEl.style.height = '0';
        textareaEl.style.height = `${textareaEl.scrollHeight}px`;
      });
    }, [draftValue, isEditing]);

    const { slots } = useSlots(children, {
      leadingAdornment: { Component: EditableTextLeadingAdornmentSlot },
    });
    const hasLeadingAdornment = Boolean(slots.leadingAdornment);

    const saveTextUpdate = useCallback(
      (options: { disableFocusBehavior?: boolean } = {}) => {
        if (!isEditing) return; // prevent duplicative saves in onBlur scenarios
        setIsEditing(false);
        if (!options?.disableFocusBehavior) {
          requestAnimationFrame(() => {
            if (editButtonRef.current) {
              editButtonRef.current.focus();
            }
          });
        }
        if (!isControlled) {
          setInternalValue(draftValue);
        }

        const trimmedValue = draftValue.trim();
        onInputChange?.(trimmedValue);
        setAnnouncement(saveAnnouncement!);
        onSave?.(trimmedValue);
      },
      [draftValue, isControlled, onInputChange, saveAnnouncement, isEditing, editButtonRef, onSave],
    );

    const cancelTextUpdate = useCallback(() => {
      setDraftValue('');
      setIsEditing(false);
      requestAnimationFrame(() => {
        if (editButtonRef.current) {
          editButtonRef.current.focus();
        }
      });
      onCancel?.();
      setAnnouncement(cancelAnnouncement!);
    }, [cancelAnnouncement, onCancel]);

    const handleEditButtonClick = () => {
      setDraftValue(currentValue);
      setIsEditing(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setDraftValue(newValue);
      onInputChange?.(newValue);
    };

    const handleInputWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node) || disableSaveOnBlur) return;

      saveTextUpdate({ disableFocusBehavior: true }); // prevent focus jump so you can tab right through the input in isEditing mode
    };

    const sharedInputProps = useMemo(
      () => ({
        id,
        disabled,
        'aria-invalid': invalid || undefined,
        placeholder: inputPlaceholder,
        value: draftValue,
        className: cn(textVariants({ color, variant }), editableInputVariants({ size, multiline, hasLeadingAdornment }), inputClassName),
        onChange: handleInputChange,
        ...props,
      }),
      [id, disabled, invalid, inputPlaceholder, draftValue, color, variant, size, multiline, inputClassName, handleInputChange, props],
    );

    if (multiline) {
      const multilineVariants = ['heading-1', 'body-1', 'body-2', 'label-1', 'label-2', 'badge-1'];
      if (!multilineVariants.includes(variant!)) {
        console.error('[Spellbook] EditableText: Multiline editing is only supported for heading, body, label, or badge variants.');
        return null;
      }
      if (type !== 'text') {
        console.error('[Spellbook] EditableText: Multiline editing is only supported for type="text".');
        return null;
      }
    }

    return (
      <div
        ref={editableWrapperRef}
        data-spellbook-name="editable-text"
        className={cn(editableWrapperVariants({ fullWidth, size, multiline, invalid, truncate, disabled }), className)}
        {...props}
      >
        <div className="flex items-center gap-2 w-full max-w-full">
          <Text
            ref={textRef}
            data-spellbook-name="editable-text-display"
            element={element}
            color={color}
            variant={variant}
            className={cn(
              editableTextVariants({
                multiline,
                truncate,
                isEditing,
                size,
                hasLeadingAdornment,
                hasValue: Boolean(currentValue),
                disabled,
              }),
              textClassName,
            )}
          >
            {(isEditing ? draftValue + '\u00A0' /* ensure a new line calculates height correctly */ : currentValue) || textPlaceholder}
          </Text>

          {!disabled && (
            <IconButton
              size="sm"
              ref={editButtonRef}
              variant="ghost"
              color="neutral"
              name="pencil-line"
              className={editIconButtonVariants({ size, isEditing, multiline })}
              onClick={handleEditButtonClick}
              aria-label={editButtonLabel}
              tabIndex={isEditing ? -1 : 0}
              aria-controls={id}
            />
          )}
        </div>
        <div
          className={editableInputWrapperVariants({ size, multiline, invalid, truncate, isEditing })}
          inert={!isEditing}
          onBlur={handleInputWrapperBlur}
        >
          {slots.leadingAdornment && (
            <div className={editableAdornmentVariants({ multiline, leading: true })}>{slots.leadingAdornment}</div>
          )}
          <label htmlFor={id} className="sr-only">
            {inputLabel}
          </label>
          {multiline ? (
            <textarea
              {...sharedInputProps}
              data-spellbook-name="editable-text-textarea"
              ref={textAreaRef}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  if (!disableCancelOnEscape) {
                    cancelTextUpdate();
                  }
                }
              }}
            />
          ) : (
            <input
              {...sharedInputProps}
              data-spellbook-name="editable-text-input"
              ref={inputRef}
              type={type}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!disableSaveOnEnter) {
                    saveTextUpdate();
                  }
                } else if (e.key === 'Escape') {
                  if (!disableCancelOnEscape) {
                    cancelTextUpdate();
                  }
                }
              }}
            />
          )}
          <div className={editableAdornmentVariants({ multiline, leading: false })}>
            <IconButton size="sm" variant="ghost" color="primary" name="check" label={saveButtonLabel} onClick={() => saveTextUpdate()} />
            <IconButton size="sm" variant="ghost" color="neutral" name="x" label={cancelButtonLabel} onClick={() => cancelTextUpdate()} />
          </div>
        </div>
        <output className="sr-only" aria-live="polite">
          {announcement}
        </output>
      </div>
    );
  },
);

/**
 * An inline text editing component that provides seamless transitions between display and edit modes.
 * Supports both controlled and uncontrolled modes, single-line and multiline editing, and includes
 * built-in accessibility features with screen reader announcements. The edit button appears on hover
 * and is keyboard accessible. Size is automatically determined based on text variant (display-1/heading-1 → 'sm', others → 'xs').
 *
 * ### When to use
 * - Editing a short piece of displayed text in place (titles, names, labels) without a full form
 *
 * ### When NOT to use
 * - Numeric values: use `EditableNumberInput`
 * - Values entered inside a form: use `TextInput` or `Textarea`
 * - Long or formatted content: use the rich text editor
 *
 * ### Component structure
 * ```tsx
 * <EditableText>
 *   <EditableText.LeadingAdornment /> // Optional, displayed during edit mode
 * </EditableText>
 * ```
 *
 * ### Available slots
 * - `<EditableText.LeadingAdornment>`: (optional) Renders an adornment before the input field during edit mode. Typically used for icons or buttons.
 *
 * ### Key functional properties
 * - `value`: The current value of the editable text. When provided, the component becomes controlled.
 * - `defaultValue`: The default value for uncontrolled usage.
 * - `onInputChange`: Callback fired when the input value changes during editing. Receives the new value as an argument.
 * - `onSave`: Callback fired when the value is saved via Enter key or Save button.
 * - `onCancel`: Callback fired when editing is cancelled via Escape key or Cancel button.
 * - `isEditing`: Whether the component is in edit mode. When provided, the component becomes controlled for editing state.
 * - `onIsEditingChange`: Callback fired when the editing state changes. Receives a boolean indicating the new editing state.
 * - `multiline`: Whether to use a multiline textarea instead of a single-line input. Only supported with 'heading-1', 'body-1', 'body-2', 'label-1', 'label-2', or 'badge-1' text variants and type 'text'.
 * - `truncate`: Whether to truncate text with ellipsis when it overflows. Only applies to single-line mode. Defaults to false.
 * - `invalid`: Whether to show the invalid state (error outline).
 * - `type`: The input type. Only 'text' is supported for multiline mode. Defaults to 'text'.
 * - `text`: Configuration object for labels, placeholders, and screen reader announcements.
 * - `variant`: Text variant from the Text component. Determines visual style and automatically sets size.
 * - `color`: Text color from the Text component.
 * - `element`: The HTML element to render for the display text. Defaults to 'span'.
 * - `textClassName`: Additional class name to apply to the display text element.
 * - `inputClassName`: Additional class name to apply to the input/textarea element.
 * - `fullWidth`: Whether the component should take the full width of its container.
 * - `showEditButtonUnlessHovering`: Whether to show the edit button unless hovering.
 * - `disableSaveOnBlur`: Whether to disable saving changes when the input loses focus.
 * - `disableCancelOnEscape`: Whether to disable cancelling edits when pressing the Escape key.
 * - `disableSaveOnEnter`: Whether to disable saving changes when pressing the Enter key. (single line only)
 * - `disabled`: Whether the component is disabled. When true, the edit button is hidden.
 *
 * **Keyboard shortcuts**: Enter to save (single-line only), Escape to cancel.
 *
 * **Accessibility**: Edit button has aria-controls pointing to the input field. Input field has a screen-reader-only label.
 * Status announcements via aria-live regions for entering edit mode, saving, and cancelling. Full keyboard navigation support with proper focus management.
 */
export const EditableText = Object.assign(EditableTextBase, {
  LeadingAdornment: EditableTextLeadingAdornmentSlot,
});
